import React, { useEffect, useState } from 'react';
import { Table, Input, Breadcrumb, Modal, message, Button, DatePicker, Space, Grid } from 'antd';
import { Link } from 'react-router-dom';
import { DeleteOutlined, RightOutlined, FileExcelOutlined, FilePdfOutlined, DownloadOutlined, SearchOutlined } from '@ant-design/icons';
import { transactionsService } from '../../common/services';
import dayjs from 'dayjs';
import moment from 'moment';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Can from '../../components/can';

const { RangePicker } = DatePicker;

const HistoriqueTransferts = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setLoading] = useState(false);

  const [q, setQ] = useState('');
  const [dateRange, setDateRange] = useState(null);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sort, setSort] = useState('-date');

  // ✅ Responsive helpers
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;                          // < md
  const controlSize = isMobile ? 'small' : 'middle';
  const fullW = { width: isMobile ? '100%' : 'auto' };

  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    let local = phone.replace('+225', '').trim();
    return local.replace(/(\d{2})(?=\d)/g, '$1 ').trim();
  };

  const fetchList = async () => {
    try {
      setLoading(true);
      const params = {
        page, limit, sort,
        q: q || undefined,
        start: dateRange?.[0]?.format('YYYY-MM-DD'),
        end: dateRange?.[1]?.format('YYYY-MM-DD'),
      };
      const data = await transactionsService.transfertsHistoriqueList(params);
      if (Array.isArray(data)) {
        setItems(data);
        setTotal(data.length);
      } else {
        setItems(data.items || []);
        setTotal(data.total || 0);
      }
    } catch (e) {
      console.error("Erreur chargement transferts:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, sort, q, dateRange]);

  // ✅ Modal.confirm responsive
  const handleDelete = (row) => {
    Modal.confirm({
      title: "Confirmer la suppression",
      content: (
        <>
          <p>Supprimer ce transfert ?</p>
          <p><strong>Expéditeur :</strong> {formatPhoneNumber(row.senderPhone)}</p>
          <p><strong>Destinataire :</strong> {formatPhoneNumber(row.receiverPhone)}</p>
        </>
      ),
      okText: "Oui, supprimer",
      cancelText: "Annuler",
      okType: "danger",
      centered: true,
      width: isMobile ? 360 : 520,
      bodyStyle: { padding: isMobile ? 12 : 24 },
      okButtonProps: { danger: true, size: controlSize, style: fullW },
      cancelButtonProps: { size: controlSize, style: fullW },
      onOk: async () => {
        try {
          await transactionsService.deleteHistoriqueTransfertById(row._id);
          message.success("Transfert supprimé !");
          fetchList();
        } catch (e) {
          console.error(e);
          message.error("Suppression impossible.");
        }
      }
    });
  };

  // Export (tout l'ensemble filtré)
  const fetchAllForExport = async () => {
    const params = {
      page: 1, limit: 5000, sort,
      q: q || undefined,
      start: dateRange?.[0]?.format('YYYY-MM-DD'),
      end: dateRange?.[1]?.format('YYYY-MM-DD'),
    };
    const data = await transactionsService.transfertsHistoriqueList(params);
    return Array.isArray(data) ? data : (data.items || []);
  };

  const exportExcel = async () => {
    const all = await fetchAllForExport();
    const rows = all.map(it => ({
      Expéditeur: it.senderName || '',
      'Tél. expéditeur': formatPhoneNumber(it.senderPhone || ''),
      Destinataire: it.receiverName || '',
      'Tél. destinataire': formatPhoneNumber(it.receiverPhone || ''),
      Montant: it.amount ?? 0,
      Date: it.dateStr || moment(it.date).format('DD/MM/YYYY HH:mm'),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Transferts');
    XLSX.writeFile(wb, 'transferts.xlsx');
  };

  const exportCSV = async () => {
    const all = await fetchAllForExport();
    const rows = all.map(it => ({
      Expéditeur: it.senderName || '',
      Tel_Expediteur: formatPhoneNumber(it.senderPhone || ''),
      Destinataire: it.receiverName || '',
      Tel_Destinataire: formatPhoneNumber(it.receiverPhone || ''),
      Montant: it.amount ?? 0,
      Date: it.dateStr || moment(it.date).format('DD/MM/YYYY HH:mm'),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const csv = XLSX.utils.sheet_to_csv(ws);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, 'transferts.csv');
  };

  const exportPDF = async () => {
    const all = await fetchAllForExport();
    const doc = new jsPDF();
    doc.text('Historique des transferts', 14, 10);
    autoTable(doc, {
      startY: 20,
      head: [['Expéditeur', 'Tél. expéd.', 'Destinataire', 'Tél. dest.', 'Montant', 'Date']],
      body: all.map(it => [
        it.senderName || '',
        formatPhoneNumber(it.senderPhone || ''),
        it.receiverName || '',
        formatPhoneNumber(it.receiverPhone || ''),
        String(it.amount ?? 0),
        it.dateStr || moment(it.date).format('DD/MM/YYYY HH:mm')
      ]),
    });
    doc.save('transferts.pdf');
  };

  const columns = [
    { title: 'Expéditeur', dataIndex: 'senderName', key: 'senderName', sorter: true },
    { title: 'Tél. Expéditeur', dataIndex: 'senderPhone', key: 'senderPhone', sorter: true, render: v => formatPhoneNumber(v) },
    { title: 'Destinataire', dataIndex: 'receiverName', key: 'receiverName', sorter: true },
    { title: 'Tél. Destinataire', dataIndex: 'receiverPhone', key: 'receiverPhone', sorter: true, render: v => formatPhoneNumber(v) },
    { title: 'Montant (F CFA)', dataIndex: 'amount', key: 'amount', sorter: true },
    { title: 'Date', dataIndex: 'date', key: 'date', sorter: true, render: (d, row) => row.dateStr || dayjs(d).format('DD/MM/YYYY HH:mm') },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: 8 }}>
          <Can perm="transfers.history:delete">
            <Button danger icon={<DeleteOutlined />} size="small" onClick={() => handleDelete(record)} />
          </Can>
        </div>
      ),
    },
  ];

  const onTableChange = (pagination, _filters, sorter) => {
    setPage(pagination.current || 1);
    setLimit(pagination.pageSize || 10);
    if (sorter?.field || sorter?.columnKey) {
      const key = sorter.columnKey || sorter.field;
      const dir = sorter.order === 'ascend' ? '' : '-';
      if (['date','amount','senderName','receiverName','senderPhone','receiverPhone'].includes(key)) {
        setSort(`${dir}${key}`);
      }
    }
  };

  return (
    <div className="content-wrapper">
      <div className="container-xxl flex-grow-1 container-p-y">
        <div className="row"><div className="col-md-12">
          <Breadcrumb separator={<RightOutlined style={{ fontSize: 16, color: '#999', fontWeight: 'bolder' }} />} style={{ marginBottom: 16 }}>
            <Breadcrumb.Item><Link to="/dashboard" style={{ fontWeight: 'bolder' }}>🏠 ACCUEIL</Link></Breadcrumb.Item>
            <Breadcrumb.Item><span style={{ fontWeight: 'bolder' }}>💸 TRANSFERTS UV</span></Breadcrumb.Item>
          </Breadcrumb>

          <div className="card mb-4"><div className="card-body">
            {/* 🔎 Filtres (stack en mobile) */}
            <div className="d-flex justify-content-between mb-3 flex-wrap gap-2">
              <Input
                prefix={<SearchOutlined />}
                placeholder="Rechercher (tous champs)"
                value={q}
                onChange={(e)=>{setQ(e.target.value); setPage(1);}}
                style={{ width: isMobile ? '100%' : 320 }}
                size={controlSize}
                allowClear
              />
              <RangePicker
                onChange={(r)=>{setDateRange(r); setPage(1);}}
                placeholder={['Date début','Date fin']}
                format="DD/MM/YYYY"
                allowClear
                size={controlSize}
                style={{ width: isMobile ? '100%' : 'auto' }}
              />
            </div>

            {/* ⬇️ Export responsive */}
            <div style={{ marginBottom: 16 }}>
              <Space wrap size={[8, 8]} style={{ width: '100%' }}>
                <Button icon={<FileExcelOutlined />} onClick={exportExcel} type="primary" size={controlSize} style={fullW}>
                  Exporter Excel
                </Button>
                <Button icon={<DownloadOutlined />} onClick={exportCSV} size={controlSize} style={fullW}>
                  Exporter CSV
                </Button>
                <Button icon={<FilePdfOutlined />} onClick={exportPDF} danger size={controlSize} style={fullW}>
                  Exporter PDF
                </Button>
              </Space>
            </div>

            <Table
              rowKey="_id"
              columns={columns}
              dataSource={items}
              loading={isLoading}
              onChange={onTableChange}
              size={controlSize}
              scroll={{ x: true }}
              pagination={{
                current: page,
                pageSize: limit,
                total,
                showSizeChanger: true,
                pageSizeOptions: [5,10,20,50,100],
                showLessItems: isMobile,
              }}
            />
          </div></div>
        </div></div>
      </div>
    </div>
  );
};

export default HistoriqueTransferts;
